// src/components/comments/Comment.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { deleteComment } from "../../redux/comments/commentActions";
import ActionDropdown from "../../components/ActionDropdown/ActionDropdown";
import { Link } from "react-router-dom";
import PublicAvatarDisplay from "../PublicProfile/utils/utils/PublicAvatarDisplay";

const formatDistanceToNow = (date, options = {}) => {
  const now = new Date();
  const diffInMs = now - new Date(date);
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInSeconds < 60) return "just now";
  if (diffInMinutes < 60)
    return `${diffInMinutes}m${options.addSuffix ? " ago" : ""}`;
  if (diffInHours < 24)
    return `${diffInHours}h${options.addSuffix ? " ago" : ""}`;
  if (diffInDays < 7)
    return `${diffInDays}d${options.addSuffix ? " ago" : ""}`;
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4)
    return `${diffInWeeks}w${options.addSuffix ? " ago" : ""}`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12)
    return `${diffInMonths}mo${options.addSuffix ? " ago" : ""}`;
  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears}y${options.addSuffix ? " ago" : ""}`;
};

const Comment = ({ comment, onLikeToggle, onDelete, onReport }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.profile?.user);

  // optimistic like state
  const [isLiked, setIsLiked] = useState(!!comment.is_liked);
  const [likeCount, setLikeCount] = useState(comment.like_count || 0);
  const [justLiked, setJustLiked] = useState(false);

  useEffect(() => {
    setIsLiked(!!comment.is_liked);
  }, [comment.is_liked]);

  useEffect(() => {
    setLikeCount(comment.like_count || 0);
  }, [comment.like_count]);

  const handleLike = () => {
    const next = !isLiked;
    setIsLiked(next);
    if (next) setJustLiked(true);
    setLikeCount((prev) => (next ? prev + 1 : Math.max(0, prev - 1)));
    onLikeToggle(comment.id, isLiked);
  };

  const handleDelete = () => {
    if (window.confirm(t('stockers.comment.deleteConfirm'))) {
      dispatch(deleteComment(comment.id));
      onDelete?.(comment.id);
    }
  };

  const handleReport = () => {
    //console.log("Reporting comment:", comment.id);
    onReport?.(comment.id);
  };

  // Check if current user is the owner of this comment
  const isOwner =
    currentUser?.id === comment.user_id || currentUser?.id === comment.author_id;

  // Get username + profile link
  const profileSlug = comment.username || comment.user?.username || "anonymous";
  const userId = comment.user?.id || comment.user_id;

  return (
    <div className="flex space-x-3 p-3 bg-gray-800/30 rounded-lg border border-gray-700/30">
      {/* ✅ Avatar with link */}
      <Link to={`/public-profiles/${encodeURIComponent(profileSlug)}`}>
        <PublicAvatarDisplay userId={userId} size="w-8 h-8" />
      </Link>

      <div className="flex-grow">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-2">
            {/* ✅ Username with link */}
            <Link
              to={`/public-profiles/${encodeURIComponent(profileSlug)}`}
              className="text-sm font-medium text-gray-300 hover:text-blue-400"
            >
              {profileSlug}
            </Link>
            <span className="text-xs text-gray-500">
              {comment.created_at
                ? formatDistanceToNow(new Date(comment.created_at), {
                    addSuffix: true,
                  })
                : t('stockers.comment.now')}
            </span>
          </div>

          <ActionDropdown
            isOwner={isOwner}
            allowEdit={false}
            allowDelete={true}
            allowReport={true}
            onDelete={handleDelete}
            onReport={handleReport}
          />
        </div>

        <p className="text-sm text-gray-400">{comment.content}</p>

        <div className="flex items-center space-x-4 mt-2">
          <button
            className="flex items-center space-x-1 text-xs text-gray-500 hover:text-purple-400 transition-colors duration-200"
            onClick={handleLike}
          >
            <svg
              className={`w-3 h-3 ${justLiked ? 'animate-pop' : ''}`}
              fill={isLiked ? "currentColor" : "none"}
              viewBox="0 0 24 24"
              stroke="currentColor"
              onAnimationEnd={() => setJustLiked(false)}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{likeCount}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Comment;
